import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { AlertCircle } from 'lucide-react';
import { patientSchema, type PatientFormData } from '@/utils/validation';
import { 
  Patient,
  PatientSex,
  BirthOutcome,
  CHDSubgroup,
  PrenatalMethod,
  Outcome30Day,
  MajorMinorFlag,
  ProcedureType,
  DataSource,
  getPatientSexLabel,
  getBirthOutcomeLabel,
  getCHDSubgroupLabel,
  getPrenatalMethodLabel,
  getOutcome30DayLabel,
  getMajorMinorFlagLabel,
  getProcedureTypeLabel,
  getDataSourceLabel
} from '@/types/patient.types';
import { useAuth } from '@/contexts/AuthContext';
import { useSectionContext } from '@/contexts/SectionContext';
import { patientService } from '@/services/patientService';
import { useToast } from '@/hooks/use-toast';
import { Loader2, AlertTriangle, Heart, User, Activity, Baby, FileText, Stethoscope, TrendingUp, Phone, Syringe } from 'lucide-react';
import { DiagnosticSection } from './DiagnosticSection';
import { PatientProceduresSection } from './PatientProceduresSection';
import { GeneticConditionAutocomplete } from '@/components/common/GeneticConditionAutocomplete';
import { MalformationCodeAutocomplete } from '@/components/common/MalformationCodeAutocomplete';
import { getDetaliiFromCNP, formatDateForInput } from '@/utils/cnp';
import { SearchableSelect } from '@/components/common/SearchableSelect';
import { LocalitateAutocomplete } from '@/components/common/LocalitateAutocomplete';
import { referenceService } from '@/services/referenceService';
import { HospitalOption, Judet } from '@/types/reference.types';
import { Diagnosis } from '@/types/diagnosis.types';
import { Lock } from 'lucide-react';

// Field labels mapping for error display
const fieldLabels: Record<string, string> = {
  name: 'Nume complet',
  birth_date: 'Data nașterii',
  gender: 'Sex',
  hospital_id: 'Spital',
  cnp: 'CNP',
  birth_weight: 'Greutate la naștere',
  gestational_age: 'Vârstă gestațională',
  patient_code: 'Cod pacient',
  external_id: 'ID foaie observație (MRN)',
  county: 'Județ',
  locality: 'Localitate',
  maternity: 'Maternitate',
  mother_name: 'Nume mamă',
  father_name: 'Nume tată',
  notes: 'Observații',
  // MCC Registry fields
  case_id: 'ID caz Registry',
  centre: 'Centru',
  sex: 'Sex MCC',
  ga_weeks: 'Vârstă gestațională (săptămâni)',
  bw_grams: 'Greutate (grame)',
  birth_outcome: 'Rezultat naștere',
  subgroup_chd: 'Subgrup MCC',
  icd10_code: 'Cod ICD-10',
  severe_chd: 'MCC sever',
  mca_flag: 'Anomalii congenitale multiple',
  syndrome: 'Altă afecțiune genetică',
  genetic_condition_icd10_code: 'Afecțiune genetică (cod ICD-10)',
  prenatal_dx: 'Diagnostic prenatal',
  prenatal_method: 'Metodă diagnostic prenatal',
  ga_prenatal_dx: 'Vârstă gestațională la diagnostic',
  outcome_30d: 'Rezultat la 30 zile',
  reintervention_1y: 'Reintervenție la 1 an',
  procedure_type: 'Procedură',
  procedure_date: 'Data procedurii',
  major_minor_flag: 'Severitate procedură',
  data_source: 'Sursă date',
  data_quality_note: 'Notă calitate date',
  other_anomalies_icd10: 'Alte anomalii ICD-10',
  'maternal_data.maternal_age': 'Vârstă maternă',
  'maternal_data.maternal_conditions_other': 'Detalii patologii materne',
  'maternal_data.exposures_other_details': 'Detalii expunere',
  'maternal_data.folate_supplementation': 'Folat periconcepțional',
};

type PatientFormSubmitOptions = {
  closeDialog: boolean;
};

// 'all' shows every tab; 'general' only the general and contact tabs; 'mcc' only the
// medical, maternal and MCC registry tabs (opened from the heart icon in the patient list).
export type PatientFormTabsMode = 'all' | 'general' | 'mcc';

interface PatientFormProps {
  patient?: Patient | null;
  tabsMode?: PatientFormTabsMode;
  onSubmit: (patient: Patient, options: PatientFormSubmitOptions) => Promise<void>;
  onCancel: () => void;
}

const buildDefaultValues = (
  patient: Patient | null | undefined,
  fallbackHospitalId?: number
): PatientFormData => {
  return {
    name: patient?.name || '',
    birth_date: patient?.birth_date || '',
    gender: patient?.gender ?? 0,
    birth_weight: patient?.birth_weight || undefined,
    gestational_age: patient?.gestational_age || undefined,
    hospital_id: patient?.hospital_id || fallbackHospitalId || 1,
    patient_code: patient?.patient_code || '',
    external_id: patient?.external_id || '',
    cnp: patient?.cnp || '',
    county: patient?.county || '',
    locality: patient?.locality || '',
    maternity: patient?.maternity || '',
    id_judet: patient?.id_judet ?? null,
    id_localitate: patient?.id_localitate ?? null,
    mother_name: patient?.mother_name || '',
    father_name: patient?.father_name || '',
    notes: patient?.notes || '',

    case_id: patient?.case_id || undefined,
    centre: patient?.centre || undefined,
    sex: patient?.sex || undefined,
    ga_weeks: patient?.ga_weeks || undefined,
    bw_grams: patient?.bw_grams || undefined,
    birth_outcome: patient?.birth_outcome || undefined,
    subgroup_chd: patient?.subgroup_chd || undefined,
    icd10_code: patient?.icd10_code || '',
    severe_chd: patient?.severe_chd ? Boolean(patient.severe_chd) : false,
    mca_flag: patient?.mca_flag ? Boolean(patient.mca_flag) : false,
    syndrome: patient?.syndrome || '',
    genetic_condition_icd10_code: patient?.genetic_condition_icd10_code ?? null,
    prenatal_dx: patient?.prenatal_dx ? Boolean(patient.prenatal_dx) : false,
    prenatal_method: patient?.prenatal_method || undefined,
    ga_prenatal_dx: patient?.ga_prenatal_dx || undefined,
    outcome_30d: patient?.outcome_30d || undefined,
    reintervention_1y: patient?.reintervention_1y ? Boolean(patient.reintervention_1y) : false,
    procedure_type: patient?.procedure_type || undefined,
    procedure_date: patient?.procedure_date || '',
    major_minor_flag: patient?.major_minor_flag || undefined,
    data_source: patient?.data_source || undefined,
    data_quality_note: patient?.data_quality_note || '',
    other_anomalies_icd10: patient?.other_anomalies_icd10 || '',

    maternal_data: {
      maternal_age: patient?.maternal_data?.maternal_age ?? null,
      maternal_conditions_diabetes: patient?.maternal_data?.maternal_conditions_diabetes ?? false,
      maternal_conditions_lupus: patient?.maternal_data?.maternal_conditions_lupus ?? false,
      maternal_conditions_hta: patient?.maternal_data?.maternal_conditions_hta ?? false,
      maternal_conditions_other_active:
        patient?.maternal_data?.maternal_conditions_other_active ?? Boolean(patient?.maternal_data?.maternal_conditions_other),
      maternal_conditions_other: patient?.maternal_data?.maternal_conditions_other ?? '',
      exposures_smoking: patient?.maternal_data?.exposures_smoking ?? false,
      exposures_teratogenic_meds: patient?.maternal_data?.exposures_teratogenic_meds ?? false,
      exposures_infection: patient?.maternal_data?.exposures_infection ?? false,
      exposures_other: patient?.maternal_data?.exposures_other ?? false,
      exposures_other_details: patient?.maternal_data?.exposures_other_details ?? '',
      folate_supplementation: patient?.maternal_data?.folate_supplementation ?? undefined,
    },
  } as PatientFormData;
};

export const PatientForm: React.FC<PatientFormProps> = ({
  patient,
  tabsMode = 'all',
  onSubmit,
  onCancel,
}) => {
  const { utilizator } = useAuth();
  const { activeSection } = useSectionContext();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = React.useState(false);
  const [activePatient, setActivePatient] = React.useState<Patient | null>(patient ?? null);
  const [activeTab, setActiveTab] = React.useState<string>(tabsMode === 'mcc' ? 'medical' : 'general');

  // Check if user is maternity level
  const isMaternityUser = utilizator?.user_level_id === 1 || utilizator?.user_level?.id === 1;
  // Check if we're in prematuri section
  const isPrematuriSection = activeSection === 'prematuri';
  // Maternity users can edit maternal conditions and basic medical data, but not MCC registry data
  // This restriction applies both when adding new patients and editing existing ones
  // Also hide MCC tabs in prematuri section
  const restrictedCHDTabs = isMaternityUser;
  const hideCHDTabs = isPrematuriSection;
  const showGeneralTabs = tabsMode !== 'mcc';
  const showMedicalTabs = tabsMode !== 'general';

  // State for CNP-locked fields and judete list
  const [isCNPPopulated, setIsCNPPopulated] = React.useState(false);
  const [isJudetFromCNP, setIsJudetFromCNP] = React.useState(false);
  const [judete, setJudete] = React.useState<Judet[]>([]);
  const [hospitals, setHospitals] = React.useState<HospitalOption[]>([]);

  // Primary diagnosis ICD-10 code, kept in sync from the "Diagnostice MCC" table so the
  // read-only "Cod ICD-10 MCC principal" line and the icd10_code form field stay accurate.
  const [primaryDiagnosisCode, setPrimaryDiagnosisCode] = React.useState<string | null | undefined>(undefined);

  const form = useForm<PatientFormData>({
    resolver: zodResolver(patientSchema),
    mode: 'onChange',
    defaultValues: buildDefaultValues(patient, utilizator?.hospital_id || 1),
  });

  const handleDiagnosesChange = React.useCallback((diagnoses: Diagnosis[]) => {
    const primary = diagnoses.find((d) => d.is_primary);
    const code = primary?.icd10_code ?? null;
    setPrimaryDiagnosisCode(code);
    form.setValue('icd10_code', code || '');
  }, [form]);

  React.useEffect(() => {
    setActivePatient(patient ?? null);
    if (patient?.id) {
      form.reset(buildDefaultValues(patient, utilizator?.hospital_id || 1));
    }
  }, [patient, form, utilizator?.hospital_id]);

  React.useEffect(() => {
    if (activePatient && activePatient.id && activePatient.id !== patient?.id) {
      form.reset(buildDefaultValues(activePatient, utilizator?.hospital_id || 1));
    }
  }, [activePatient, patient?.id, form, utilizator?.hospital_id]);

  // Load judete on mount
  React.useEffect(() => {
    const loadJudete = async () => {
      try {
        const data = await referenceService.getJudete();
        setJudete(data);
      } catch (error) {
        console.error('Failed to load judete:', error);
        toast({
          title: 'Eroare',
          description: 'Nu s-au putut încărca județele.',
          variant: 'destructive'
        });
      }
    };
    loadJudete();
  }, [toast]);

  React.useEffect(() => {
    const loadHospitals = async () => {
      const data = await referenceService.getHospitals();
      setHospitals(Array.isArray(data) ? data : []);
    };
    void loadHospitals();
  }, []);

  // Check if patient has CNP and set lock state
  React.useEffect(() => {
    if (patient?.cnp && patient.cnp.length === 13) {
      setIsCNPPopulated(true);
      const details = getDetaliiFromCNP(patient.cnp);
      setIsJudetFromCNP(details ? !details.isCnpNou : false);
    } else {
      setIsCNPPopulated(false);
      setIsJudetFromCNP(false);
    }
  }, [patient?.cnp]);

  const [watchedName, watchedBirthDate, watchedGender, watchedHospitalId] = form.watch([
    'name',
    'birth_date',
    'gender',
    'hospital_id',
  ]);

  // Handler for CNP changes - auto-populate gender, birth date, and judet
  const handleCNPChange = (value: string) => {
    // Update the CNP field value
    form.setValue('cnp', value);

    // If CNP has 13 digits, try to extract details
    if (value.length === 13) {
      const details = getDetaliiFromCNP(value);
      if (details) {
        // Auto-populate gender (0 = female, 1 = male)
        form.setValue('gender', details.sex);

        // Auto-populate birth date
        const formattedDate = formatDateForInput(details.dateNastere);
        form.setValue('birth_date', formattedDate);

        if (!details.isCnpNou) {
          // CNP normal - set and lock judet
          if (details.id_judet) {
            form.setValue('id_judet', details.id_judet);
          }
          if (details.JudetNastereRenv) {
            form.setValue('county', details.JudetNastereRenv.denumire);
          }
          form.setValue('id_localitate', null);
          form.setValue('locality', '');
          setIsJudetFromCNP(true);
        } else {
          // CNP_NOU - leave judet/localitate editable
          setIsJudetFromCNP(false);
        }

        // Lock sex and birth date fields
        setIsCNPPopulated(true);

        toast({
          title: 'Date detectate din CNP',
          description: `Sex: ${details.sex === 1 ? 'Masculin' : 'Feminin'}, Data nașterii: ${formattedDate}${!details.isCnpNou ? `, Județ: ${details.JudetNastereRenv?.denumire || 'necunoscut'}` : ', Județ: selectați manual (CNP nou)'}`,
        });
      } else {
        // Invalid CNP, unlock fields
        setIsCNPPopulated(false);
        setIsJudetFromCNP(false);
      }
    } else {
      // CNP cleared or incomplete, unlock fields
      setIsCNPPopulated(false);
      setIsJudetFromCNP(false);
    }
  };

  const missingRequiredFields = useMemo(() => {
    const requiredFieldChecks = [
      {
        label: 'Nume complet',
        isValid: typeof watchedName === 'string' && watchedName.trim().length >= 2,
      },
      {
        label: 'Data nașterii',
        isValid: typeof watchedBirthDate === 'string' && watchedBirthDate.trim().length > 0,
      },
      {
        label: 'Sex',
        isValid: watchedGender === 0 || watchedGender === 1,
      },
      {
        label: 'Spital',
        isValid: typeof watchedHospitalId === 'number' && watchedHospitalId > 0,
      },
    ];

    return requiredFieldChecks
      .filter((item) => !item.isValid)
      .map((item) => item.label);
  }, [watchedName, watchedBirthDate, watchedGender, watchedHospitalId]);

  const flatValidationErrors = useMemo(() => {
    const resolveLabel = (path: string[]): string | undefined => {
      if (path.length === 0) return undefined;

      const joined = path.join('.');
      if (fieldLabels[joined]) {
        return fieldLabels[joined];
      }

      for (let i = path.length - 1; i >= 0; i--) {
        const segment = path[i];
        if (fieldLabels[segment]) {
          return fieldLabels[segment];
        }
      }

      return joined;
    };

    const collectMessages = (value: any, path: string[] = []): string[] => {
      if (!value) return [];

      const messages: string[] = [];

      if (
        typeof value === 'object' &&
        'message' in value &&
        typeof value.message === 'string' &&
        value.message.trim().length > 0
      ) {
        const label = resolveLabel(path);
        messages.push(label ? `${label}: ${value.message}` : value.message.trim());
      }

      if (Array.isArray(value)) {
        value.forEach((item, index) => {
          messages.push(...collectMessages(item, [...path, String(index)]));
        });
        return messages;
      }

      if (typeof value === 'object' && value !== null) {
        Object.entries(value).forEach(([key, child]) => {
          if (key === 'message' || key === 'type' || key === 'ref') {
            return;
          }
          messages.push(...collectMessages(child, [...path, key]));
        });
      }

      return messages;
    };

    return collectMessages(form.formState.errors);
  }, [form.formState.errors]);

  const showMissingFieldsAlert = missingRequiredFields.length > 0;
  const showValidationErrorsAlert = flatValidationErrors.length > 0;
  const shouldDisplayAlert = showMissingFieldsAlert || showValidationErrorsAlert;
  const isSubmitDisabled = isLoading || !form.formState.isValid;

  const handleSubmit = async (data: PatientFormData) => {
    try {
      setIsLoading(true);
      
      // Clean up the data - remove empty strings for optional fields
      const cleanedData = {
        ...data,
        patient_code: data.patient_code || undefined,
        cnp: data.cnp || undefined,
        county: data.county || undefined,
        locality: data.locality || undefined,
        maternity: data.maternity || undefined,
        mother_name: data.mother_name || undefined,
        father_name: data.father_name || undefined,
        notes: data.notes || undefined,
        external_id: data.external_id || undefined,
        icd10_code: data.icd10_code || undefined,
        syndrome: data.syndrome || undefined,
        genetic_condition_icd10_code: data.genetic_condition_icd10_code || null,
        data_quality_note: data.data_quality_note || undefined,
        other_anomalies_icd10: data.other_anomalies_icd10 || undefined,
        procedure_date: data.procedure_date || undefined,
      };

      if (data.maternal_data) {
        const otherDetails = data.maternal_data.maternal_conditions_other?.trim() || '';
        const exposuresOtherDetails = data.maternal_data.exposures_other_details?.trim() || '';

        const sanitizedMaternal = {
          maternal_age:
            typeof data.maternal_data.maternal_age === 'number'
              ? data.maternal_data.maternal_age
              : null,
          maternal_conditions_diabetes: Boolean(data.maternal_data.maternal_conditions_diabetes),
          maternal_conditions_lupus: Boolean(data.maternal_data.maternal_conditions_lupus),
          maternal_conditions_hta: Boolean(data.maternal_data.maternal_conditions_hta),
          maternal_conditions_other_active: Boolean(data.maternal_data.maternal_conditions_other_active),
          maternal_conditions_other:
            Boolean(data.maternal_data.maternal_conditions_other_active) && otherDetails
              ? otherDetails
              : null,
          exposures_smoking: Boolean(data.maternal_data.exposures_smoking),
          exposures_teratogenic_meds: Boolean(data.maternal_data.exposures_teratogenic_meds),
          exposures_infection: Boolean(data.maternal_data.exposures_infection),
          exposures_other: Boolean(data.maternal_data.exposures_other),
          exposures_other_details:
            Boolean(data.maternal_data.exposures_other) && exposuresOtherDetails
              ? exposuresOtherDetails
              : null,
          folate_supplementation:
            typeof data.maternal_data.folate_supplementation === 'number'
              ? data.maternal_data.folate_supplementation
              : null,
        };

        const hasMaternalValues =
          sanitizedMaternal.maternal_age !== null ||
          sanitizedMaternal.maternal_conditions_diabetes ||
          sanitizedMaternal.maternal_conditions_lupus ||
          sanitizedMaternal.maternal_conditions_hta ||
          sanitizedMaternal.maternal_conditions_other !== null ||
          sanitizedMaternal.exposures_smoking ||
          sanitizedMaternal.exposures_teratogenic_meds ||
          sanitizedMaternal.exposures_infection ||
          sanitizedMaternal.exposures_other ||
          sanitizedMaternal.folate_supplementation !== null;

        if (hasMaternalValues) {
          cleanedData.maternal_data = sanitizedMaternal;
        } else {
          delete (cleanedData as any).maternal_data;
        }
      } else {
        delete (cleanedData as any).maternal_data;
      }

      if (activePatient?.id) {
        // Update existing patient
        const updatedPatient = await patientService.updatePatient(activePatient.id, cleanedData);
        setActivePatient(updatedPatient);
        form.reset(buildDefaultValues(updatedPatient, utilizator?.hospital_id || 1));
        toast({
          title: 'Succes',
          description: 'Pacientul a fost actualizat cu succes.',
        });
        await onSubmit(updatedPatient, { closeDialog: true });
      } else {
        // Create new patient
        const createdPatient = await patientService.createPatient(cleanedData);
        const refreshedPatient = await patientService.getPatient(createdPatient.id);
        setActivePatient(refreshedPatient);
        toast({
          title: 'Succes',
          description: 'Pacientul a fost adăugat cu succes.',
        });
        form.reset(buildDefaultValues(refreshedPatient, utilizator?.hospital_id || 1));
        await onSubmit(refreshedPatient, { closeDialog: false });
      }
    } catch (error: any) {
      console.error('Patient creation/update error:', {
        error,
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      toast({
        title: 'Eroare',
        description: error.message || 'A apărut o eroare la salvarea pacientului.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        {restrictedCHDTabs && (
          <Alert className="mb-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Acces restricționat</AlertTitle>
            <AlertDescription>
              Ca utilizator de maternitate, puteți completa datele generale și medicale ale pacientului.
              Diagnosticele, codificarea internațională și procedurile sunt gestionate de spitale.
            </AlertDescription>
          </Alert>
        )}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1">
            {showGeneralTabs && (
              <TabsTrigger value="general">
                <User className="h-4 w-4 mr-1" />
                Date generale
              </TabsTrigger>
            )}
            {showMedicalTabs && (
              <>
                <TabsTrigger value="medical">
                  <Activity className="h-4 w-4 mr-1" />
                  Date medicale
                </TabsTrigger>
              </>
            )}
            {showMedicalTabs && !hideCHDTabs && (
              <>
                <TabsTrigger value="prenatal" disabled={restrictedCHDTabs}>
                  <Stethoscope className="h-4 w-4 mr-1" />
                  Diagnostic prenatal {restrictedCHDTabs && '🔒'}
                </TabsTrigger>
                <TabsTrigger value="diagnostice" disabled={restrictedCHDTabs}>
                  <Heart className="h-4 w-4 mr-1" />
                  Diagnostice și codificare {restrictedCHDTabs && '🔒'}
                </TabsTrigger>
                <TabsTrigger value="proceduri" disabled={restrictedCHDTabs}>
                  <Syringe className="h-4 w-4 mr-1" />
                  Proceduri {restrictedCHDTabs && '🔒'}
                </TabsTrigger>
                <TabsTrigger value="outcomes" disabled={restrictedCHDTabs}>
                  <TrendingUp className="h-4 w-4 mr-1" />
                  Rezultate {restrictedCHDTabs && '🔒'}
                </TabsTrigger>
              </>
            )}
            {showGeneralTabs && (
              <TabsTrigger value="contact">
                <Phone className="h-4 w-4 mr-1" />
                Date contact
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="general" className="space-y-4 mt-4">
            {/* Hospital selector for users without hospital_id */}
            {!utilizator?.hospital_id && (
              <FormField
                control={form.control}
                name="hospital_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Spital *</FormLabel>
                    <Select 
                      onValueChange={(value) => field.onChange(parseInt(value))}
                      value={field.value?.toString()}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selectați spitalul" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="1">Spitalul Clinic de Urgență pentru Copii "Marie Curie"</SelectItem>
                        <SelectItem value="2">Institutul Național pentru Sănătatea Mamei și Copilului</SelectItem>
                        <SelectItem value="3">Spitalul Clinic de Copii "Dr. Victor Gomoiu"</SelectItem>
                        <SelectItem value="4">Spitalul Clinic de Urgență pentru Copii Cluj-Napoca</SelectItem>
                        <SelectItem value="5">Spitalul Clinic de Urgență pentru Copii "Sf. Maria" Iași</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Selectați spitalul unde este înregistrat pacientul
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nume complet *</FormLabel>
                    <FormControl>
                      <Input placeholder="Ex: Popescu Ion" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="patient_code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cod pacient</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="Generat automat" 
                        {...field} 
                        disabled={!!(activePatient?.id)}
                      />
                    </FormControl>
                    <FormDescription>
                      {activePatient?.id 
                        ? 'Codul pacientului nu poate fi modificat'
                        : 'Lasați gol pentru generare automată'}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="cnp"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>CNP (Cod Numeric Personal)</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Ex: 1234567890123"
                        maxLength={13}
                        value={field.value}
                        onChange={(e) => handleCNPChange(e.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                      />
                    </FormControl>
                    <FormDescription>
                      Cod Numeric Personal - 13 cifre (opțional, detectare automată sex și data nașterii)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="case_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>ID caz Registry MCC</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="UUID generat automat" 
                        {...field} 
                        disabled
                      />
                    </FormControl>
                    <FormDescription>
                      ID unic pentru registry-ul european
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="external_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>ID foaie observație (MRN)</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="ID din alt sistem" 
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      ID din sistemul spitalului sau alt sistem
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="birth_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      Data nașterii *
                      {isCNPPopulated && <Lock className="h-3 w-3 text-muted-foreground" />}
                    </FormLabel>
                    <FormControl>
                      <Input type="date" {...field} disabled={isCNPPopulated} />
                    </FormControl>
                    {isCNPPopulated && (
                      <FormDescription>
                        Blocat - extras din CNP
                      </FormDescription>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="gender"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      Sex *
                      {isCNPPopulated && <Lock className="h-3 w-3 text-muted-foreground" />}
                    </FormLabel>
                    <Select
                      onValueChange={(value) => field.onChange(parseInt(value))}
                      value={field.value?.toString()}
                      disabled={isCNPPopulated}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selectați sexul" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="0">Feminin</SelectItem>
                        <SelectItem value="1">Masculin</SelectItem>
                      </SelectContent>
                    </Select>
                    {isCNPPopulated && (
                      <FormDescription>Blocat - extras din CNP</FormDescription>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="id_judet"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      Județ
                      {isJudetFromCNP && <Lock className="h-3 w-3 text-muted-foreground" />}
                    </FormLabel>
                    <Select
                      onValueChange={(value) => {
                        const judetId = parseInt(value);
                        field.onChange(judetId);
                        // Update county name for display
                        const judet = judete.find(j => j.id_judet === judetId);
                        if (judet) {
                          form.setValue('county', judet.denumire);
                        }
                        // Clear locality when judet changes
                        form.setValue('id_localitate', null);
                        form.setValue('locality', '');
                      }}
                      value={field.value?.toString()}
                      disabled={isJudetFromCNP}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selectați județul" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="max-h-[300px]">
                        {judete.map((judet) => (
                          <SelectItem key={judet.id_judet} value={judet.id_judet.toString()}>
                            {judet.denumire}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {isJudetFromCNP && (
                      <FormDescription>
                        Blocat - extras din CNP
                      </FormDescription>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="id_localitate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Localitate</FormLabel>
                    <FormControl>
                      <LocalitateAutocomplete
                        value={field.value}
                        onChange={(idLocalitate, nume) => {
                          field.onChange(idLocalitate);
                          // Update locality name for display
                          if (nume) {
                            form.setValue('locality', nume);
                          }
                        }}
                        idJudet={form.watch('id_judet')}
                        disabled={!form.watch('id_judet')}
                      />
                    </FormControl>
                    <FormDescription>
                      {!form.watch('id_judet') ? 'Selectați județul mai întâi' : 'Căutați și selectați localitatea'}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </TabsContent>

          <TabsContent value="medical" className="space-y-4 mt-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Datele medicale</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pb-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="birth_weight"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Greutate la naștere (g)</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        placeholder="Ex: 3200"
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : null)}
                        onBlur={field.onBlur}
                        name={field.name}
                      />
                    </FormControl>
                    <FormDescription>
                      Între 300g și 6000g
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="gestational_age"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Vârstă gestațională (săptămâni)</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        placeholder="Ex: 38"
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : null)}
                        onBlur={field.onBlur}
                        name={field.name}
                      />
                    </FormControl>
                    <FormDescription>
                      Între 22 și 43 săptămâni
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="maternity"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Maternitate</FormLabel>
                  <FormControl>
                    <SearchableSelect
                      options={hospitals.map((hospital) => hospital.hospital_name)}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Selectați maternitatea"
                      searchPlaceholder="Caută spital sau maternitate..."
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Birth Outcome */}
          <FormField
            control={form.control}
            name="birth_outcome"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Rezultat naștere</FormLabel>
                <Select 
                  onValueChange={(value) => field.onChange(parseInt(value))}
                  value={field.value?.toString()}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Selectați rezultatul nașterii" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {Object.values(BirthOutcome).filter(v => typeof v === 'number').map(outcome => (
                      <SelectItem key={outcome} value={outcome.toString()}>
                        {getBirthOutcomeLabel(outcome as BirthOutcome)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Diagnostice și codificare Tab (merged) - hidden in prematuri section */}
        {!hideCHDTabs && (
          <TabsContent value="diagnostice" className="space-y-4 mt-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Diagnostice și codificare</CardTitle>
                <CardDescription>
                  Informații specifice pentru registry-ul european MCC
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 pb-4">
                <div>
                  {/* Hospital */}
                  <FormField
                    control={form.control}
                    name="centre"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Spital</FormLabel>
                        <FormControl>
                          <SearchableSelect
                            options={hospitals.map((hospital) => ({
                              value: hospital.hospital_id.toString(),
                              label: hospital.hospital_name,
                            }))}
                            value={field.value?.toString()}
                            onChange={(value) => field.onChange(parseInt(value, 10))}
                            placeholder="Selectați spitalul"
                            searchPlaceholder="Caută spital sau maternitate..."
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Checkboxes */}
                <div className="grid gap-4 md:grid-cols-3">
                  <FormField
                    control={form.control}
                    name="severe_chd"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>
                            MCC sever
                          </FormLabel>
                          <FormDescription>
                            Conform criteriilor EUROCAT
                          </FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />

                  {/* MCC Subgroup */}
                  <FormField
                    control={form.control}
                    name="subgroup_chd"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Subgrup MCC</FormLabel>
                        <Select
                          onValueChange={(value) => field.onChange(parseInt(value))}
                          value={field.value?.toString()}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selectați subgrupul" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {Object.values(CHDSubgroup).filter(v => typeof v === 'number').map(subgroup => (
                              <SelectItem key={subgroup} value={subgroup.toString()}>
                                {getCHDSubgroupLabel(subgroup as CHDSubgroup)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="mca_flag"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>
                            Anomalii congenitale multiple (MCA)
                          </FormLabel>
                          <FormDescription>
                            Prezintă anomalii congenitale multiple
                          </FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />
                </div>

                {/* Read-only status derived from the diagnosis list below. */}
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border bg-muted/30 px-4 py-3 text-sm">
                  <div>
                    <p className="text-base font-semibold leading-none tracking-tight">Diagnostic principal</p>
                    <p className="mt-1.5 text-muted-foreground">
                      Se stabilește automat din lista de diagnostice.
                    </p>
                  </div>
                  <Badge variant={primaryDiagnosisCode ? 'secondary' : 'outline'} className="font-mono">
                    {primaryDiagnosisCode || 'Nesetat'}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {activePatient?.id ? (
              <DiagnosticSection
                patientId={activePatient.id}
                onDiagnosesChange={handleDiagnosesChange}
                isReadOnly={restrictedCHDTabs}
              />
            ) : (
              <Card>
                <CardContent className="py-4">
                  <div className="flex items-start gap-3 text-muted-foreground">
                    <Heart className="mt-0.5 h-5 w-5 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Pacient nou</p>
                      <p className="text-sm">Salvați mai întâi pacientul pentru a gestiona diagnosticele MCC.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Alte malformații și afecțiuni genetice</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="other_anomalies_icd10"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Alte malformații congenitale (cod ICD-10)</FormLabel>
                      <FormControl>
                        <MalformationCodeAutocomplete
                          value={field.value ?? ''}
                          onChange={(value) => field.onChange(value)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="genetic_condition_icd10_code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Afecțiune genetică (cod ICD-10)</FormLabel>
                      <FormControl>
                        <GeneticConditionAutocomplete
                          value={field.value}
                          onChange={(code) => field.onChange(code)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="syndrome"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Altă afecțiune genetică (text)</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Descriere afecțiune genetică, dacă nu există cod ICD-10 corespunzător"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>
          </TabsContent>
        )}

          {/* Prenatal Diagnosis Tab - hidden in prematuri section */}
          {!hideCHDTabs && (
          <TabsContent value="prenatal" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Diagnostic prenatal</CardTitle>
                <CardDescription>
                  Informații despre diagnosticul prenatal al MCC
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Prenatal Diagnosis Checkbox */}
                <FormField
                  control={form.control}
                  name="prenatal_dx"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>
                          Diagnostic prenatal
                        </FormLabel>
                        <FormDescription>
                          A fost diagnosticat prenatal
                        </FormDescription>
                      </div>
                    </FormItem>
                  )}
                />

                {form.watch('prenatal_dx') && (
                  <div className="grid grid-cols-2 gap-4 ml-6">
                    {/* Prenatal Method */}
                    <FormField
                      control={form.control}
                      name="prenatal_method"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Metodă diagnostic prenatal</FormLabel>
                          <Select 
                            onValueChange={(value) => field.onChange(parseInt(value))}
                            value={field.value?.toString()}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Selectați metoda" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {Object.values(PrenatalMethod).filter(v => typeof v === 'number').map(method => (
                                <SelectItem key={method} value={method.toString()}>
                                  {getPrenatalMethodLabel(method as PrenatalMethod)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* GA at Prenatal Diagnosis */}
                    <FormField
                      control={form.control}
                      name="ga_prenatal_dx"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Vârstă gestațională la diagnostic (săptămâni)</FormLabel>
                          <FormControl>
                            <Input 
                              type="number" 
                              placeholder="Ex: 20"
                              value={field.value ?? ''}
                              onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : null)}
                            />
                          </FormControl>
                          <FormDescription>
                            Între 10 și 40 săptămâni
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          )}

          {/* Proceduri Tab - hidden in prematuri section */}
          {!hideCHDTabs && (
          <TabsContent value="proceduri" className="space-y-4 mt-4">
            <PatientProceduresSection
              patient={activePatient ?? ({ id: 0 } as Patient)}
              isReadOnly={restrictedCHDTabs}
            />
          </TabsContent>
          )}

          {/* Outcomes Tab - hidden in prematuri section */}
          {!hideCHDTabs && (
          <TabsContent value="outcomes" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Rezultate</CardTitle>
                <CardDescription>
                  Rezultate la 30 zile și follow-up la 1 an
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  {/* 30-Day Outcome */}
                  <FormField
                    control={form.control}
                    name="outcome_30d"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Rezultat la 30 zile</FormLabel>
                        <Select 
                          onValueChange={(value) => field.onChange(parseInt(value))}
                          value={field.value?.toString()}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selectați rezultatul" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {Object.values(Outcome30Day).filter(v => typeof v === 'number').map(outcome => (
                              <SelectItem key={outcome} value={outcome.toString()}>
                                {getOutcome30DayLabel(outcome as Outcome30Day)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Reintervention at 1 Year */}
                <FormField
                  control={form.control}
                  name="reintervention_1y"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>
                          Reintervenție la 1 an
                        </FormLabel>
                        <FormDescription>
                          A necesitat reintervenție în primul an de viață
                        </FormDescription>
                      </div>
                    </FormItem>
                  )}
                />

                {/* Data Source */}
                <FormField
                  control={form.control}
                  name="data_source"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sursă date</FormLabel>
                      <Select 
                        onValueChange={(value) => field.onChange(parseInt(value))}
                        value={field.value?.toString()}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selectați sursa datelor" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {Object.values(DataSource).filter(v => typeof v === 'number').map(source => (
                            <SelectItem key={source} value={source.toString()}>
                              {getDataSourceLabel(source as DataSource)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Data Quality Note */}
                <FormField
                  control={form.control}
                  name="data_quality_note"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Notă calitate date</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Observații despre calitatea sau completitudinea datelor" 
                          className="min-h-[80px]"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>
          </TabsContent>
          )}

          <TabsContent value="contact" className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="mother_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nume mamă</FormLabel>
                    <FormControl>
                      <Input placeholder="Numele mamei" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="father_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nume tată</FormLabel>
                    <FormControl>
                      <Input placeholder="Numele tatălui" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Observații</FormLabel>
                  <FormControl>
                    <textarea 
                      className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      placeholder="Observații adiționale..."
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </TabsContent>

        </Tabs>

        {/* Validation Error Alert */}
        {shouldDisplayAlert && (
          <Alert className="mb-4" variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Completați câmpurile obligatorii</AlertTitle>
            <AlertDescription>
              <div className="mt-2 space-y-2">
                {showMissingFieldsAlert && (
                  <p>
                    Câmpuri lipsă: <strong>{missingRequiredFields.join(', ')}</strong>
                  </p>
                )}
                {showValidationErrorsAlert && (
                  <ul className="list-disc list-inside space-y-1">
                    {flatValidationErrors.map((message) => (
                      <li key={message}>{message}</li>
                    ))}
                  </ul>
                )}
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Form Actions */}
        <div className="flex justify-end gap-4 pt-4 border-t">
          <Button type="button" variant="outline" onClick={onCancel}>
            Anulează
          </Button>
          <Button 
            type="submit" 
            disabled={isSubmitDisabled}
            title={isSubmitDisabled ? "Completați toate câmpurile obligatorii" : ""}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Se salvează...
              </>
            ) : (
              patient ? 'Actualizează' : 'Adaugă pacient'
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
};
